import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import useApi from 'hooks/useApi';
import {
  getDocumentDetails,
  setSelectedContacts,
  setDocumentsDetails,
} from 'redux/actions/documentActions';
import { showAlert } from 'redux/actions/alertActions';
import { refreshContacts } from 'redux/actions/contactActions';

import { Checkbox } from 'shared/components/form/CheckBox';
import { Switch } from 'shared/components/form/Inputs';
import { Button } from 'shared/components/Buttons';
import MainSpinner from 'shared/components/MainSpinner';
import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS } from 'shared/utils';
import { convertErrorsArrayToString } from 'shared/helpers/helper';

import { ReactComponent as UserIcon } from 'assets/icon-user.svg';
import { Col, Row, Container } from 'reactstrap';
import SwitchEmailToContacts from 'shared/components/SwitchEmailToContacts';
import { ReactComponent as WarningIcon } from 'assets/icon-warning.svg';
import { colors } from 'shared/defaultTheme';

const Contacts = ({ onCancel }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { customAreaApi } = useApi();
  const { cusNavId, clientId, isCustomer, mail } = useSelector((st) => st.auth);
  const { selectedContacts, documentDtails, documentsDetails, emailToContactsOnPublish } =
    useSelector((state) => state.document);
  const { currentClient } = useSelector((state) => state.clientState);
  const [contactsData, setContactsData] = useState([]);
  const [isFolderSelected, setIsFolderSelected] = useState(false);
  const [allSelected, setAllselected] = useState(false);
  const [selectedAccess, setSelectedAccess] = useState([]);
  const [selectedNoAccess, setSelectedNoAccess] = useState([]);
  const [giveAccess, setGiveAccess] = useState(false);

  const [filterNoAccess, setFilterNoAccess] = useState([]);
  const [filterAccess, setFilterAccess] = useState([]);

  useEffect(() => {
    if (!contactsData || !Array.isArray(contactsData)) return;
    const filterNoAccessArray = contactsData.filter((contact) => contact.contactState !== 'access');
    const filterAccessArray = contactsData.filter((contact) => contact.contactState === 'access');
    setFilterNoAccess(filterNoAccessArray);
    setFilterAccess(filterAccessArray);
  }, [contactsData]);

  const [, getContacts] = customAreaApi(
    {
      url: `${window.API_V1}/cli/contacts/${cusNavId}/search/all`,
      method: 'POST',
      data: {
        filters: [
          {
            model: 'ClientAccess',
            field: 'ClientId',
            filter: [currentClient.clientId || clientId],
          },
          {
            model: 'Contact',
            field: 'Name',
            asc: true,
          },
        ],
        reduce: {
          limit: 0,
          skip: 0,
        },
      },
    },
    { manual: true }
  );

  const [{ loading: loadingUpdate }, updatePublicDocument] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading }, postAccessMassive] = customAreaApi(
    {
      url: `${window.API_V1}/login/access/massive`,
      method: 'POST',
    },
    { manual: true }
  );

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const {
          data: { data },
        } = await getContacts();
        const filterContacts = data.filter((contact) => contact.mail);
        setContactsData(filterContacts);
      } catch (error) {
        console.error(`Error Contacts: ${error}`);
      }
    };
    fetchContacts();
  }, []);

  useEffect(() => {
    if (documentDtails) {
      setIsFolderSelected(!documentDtails.isFile);
    } else {
      setIsFolderSelected(documentsDetails.some((document) => !document.isFile));
    }
  }, []);

  const onGiveAccess = async (contacts) => {
    try {
      const body = contacts.map((contact) => {
        return {
          login: contact.mail,
          idClient: contact.clientId,
          idCustomer: cusNavId,
          domain: window.location.origin,
        };
      });
      const {
        data: { errors },
      } = await postAccessMassive({
        data: body,
      });
      if (errors) {
        throw new Error(convertErrorsArrayToString(errors));
      }
      dispatch(refreshContacts(true));

      dispatch(
        showAlert({
          alertType: ALERT_TYPE_SUCCESS,
          alertMessage: t('general_success_message'),
        })
      );
    } catch (ex) {
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: t('general_error_message'),
        })
      );
      console.error('error giving access: ', ex);
    }
  };

  const removeSelectedContacts = (selectedMail) => {
    const newContacts = selectedContacts.filter((x) => x.mail !== selectedMail);
    dispatch(setSelectedContacts(newContacts));
  };
  const removeSelectedContactsAccess = (selectedMail) => {
    const newContacts = selectedAccess.filter((x) => x.mail !== selectedMail);
    setSelectedAccess(newContacts);
  };
  const removeSelectedContactsNoAccess = (selectedMail) => {
    const newContacts = selectedNoAccess.filter((x) => x.mail !== selectedMail);
    setSelectedNoAccess(newContacts);
  };

  const handleSelectAllContact = () => {
    setAllselected(!allSelected);
    if (!allSelected) {
      dispatch(setSelectedContacts([...contactsData]));
    } else {
      dispatch(setSelectedContacts([]));
    }
  };

  const handleSelectAllContactAccess = () => {
    if (selectedAccess.length !== filterAccess.length) {
      setSelectedAccess([...filterAccess]);
    } else {
      setSelectedAccess([]);
    }
  };

  const handleSelectAllContactNoAccess = () => {
    if (selectedNoAccess.length !== filterNoAccess.length) {
      setSelectedNoAccess([...filterNoAccess]);
    } else {
      setSelectedNoAccess([]);
    }
  };

  const handleSelectContact = useCallback((e, contact) => {
    const existingContact = selectedContacts.some((x) => x.mail === contact.mail);
    if (!existingContact) {
      const newContacts = [...selectedContacts, contact];
      dispatch(setSelectedContacts(newContacts));
    } else {
      removeSelectedContacts(contact.mail);
    }
  });
  const handleSelectContactAccess = useCallback((e, contact) => {
    const existingContact = selectedAccess.some((x) => x.mail === contact.mail);
    if (!existingContact) {
      const newContacts = [...selectedContacts, contact];
      setSelectedAccess(newContacts);
    } else {
      removeSelectedContactsAccess(contact.mail);
    }
  });
  const handleSelectContactNoAccess = useCallback((e, contact) => {
    const existingContact = selectedNoAccess.some((x) => x.mail === contact.mail);
    if (!existingContact) {
      const newContacts = [...selectedNoAccess, contact];
      setSelectedNoAccess(newContacts);
    } else {
      removeSelectedContactsNoAccess(contact.mail);
    }
  });

  const doPublish = async () => {
    try {
      const permissions = [];

      if (selectedContacts) {
        permissions.push(
          ...selectedContacts.map((permissionsContact) => {
            return { u: permissionsContact.mail, p: 'r--' };
          })
        );
      }
      if (selectedAccess) {
        permissions.push(
          ...selectedAccess.map((permissionsItem) => {
            return { u: permissionsItem.mail, p: 'r--' };
          })
        );
      }
      if (selectedNoAccess) {
        permissions.push(
          ...selectedNoAccess.map((permissionsItem) => {
            return { u: permissionsItem.mail, p: '---' };
          })
        );
        if (giveAccess) {
          onGiveAccess(selectedNoAccess);
        }
      }
      const newData = [];

      if (documentDtails) {
        newData.push({ ...documentDtails, permissions });
      } else {
        documentsDetails.map((document) => newData.push({ ...document, permissions }));
      }

      await updatePublicDocument({
        data: newData,
        url: isCustomer
          ? `${window.API_V1}/cli/items/${cusNavId}/${
              isCustomer ? currentClient.clientId : clientId
            }/${encodeURIComponent(mail)}/publish?sendNotify=${emailToContactsOnPublish.toString()}`
          : `${window.API_V1}/cli/items/${cusNavId}/${clientId}/${encodeURIComponent(
              mail
            )}/publishAdmin`,
      });
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_SUCCESS,
          alertMessage: t('general_success_message'),
        })
      );
      dispatch(getDocumentDetails(null));
      dispatch(setDocumentsDetails([]));
      onCancel();
    } catch (error) {
      console.error('publishing documents error: ', error);
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: t('custom_field_operation_error_message'),
        })
      );
      dispatch(getDocumentDetails(null));
      onCancel();
    }
  };

  const handleChange = () => {
    setGiveAccess(!giveAccess);
  };
  return (
    <>
      <h2 className="title-modal-contacts">{t('document_contact_notifications')}</h2>
      {isFolderSelected && (
        <div className="warning-container">
          <WarningIcon className="warning-icon-contacts" width={25} height={25} />
          <span className="warning-text-contacts">{t('document_contact_folder_warning')}</span>
        </div>
      )}
      <p className="text-add-contacts">{t('document_add_contacts')}</p>
      {isFolderSelected ? (
        <>
          {!!filterAccess?.length && (
            <>
              <span className="contacts-access-status">{t('contact_with_access')}</span>
              <div className="switch-section">
                <span className="switch-label">{t('documents_select_all_clients')}</span>
                <Switch
                  className="ml-2"
                  classNameMain=""
                  id="switchSelectAllAccess"
                  color={
                    selectedAccess.length === filterAccess.length
                      ? colors.pastelGreen
                      : colors.venetianRed
                  }
                  textColor={colors.black}
                  value={selectedAccess.length !== filterAccess.length}
                  onChange={handleSelectAllContactAccess}
                />
              </div>
              <div className="contacts-box mt-2">
                <ul className="contacts-container my-3">
                  {filterAccess?.map((contact) => (
                    <li className="mb-3 list-contact" key={contact.mail}>
                      <Checkbox
                        type="checkbox"
                        id="filterCheckAll"
                        label=""
                        checked={selectedAccess.some((file) => file.mail === contact.mail)}
                        onClick={(e) => handleSelectContactAccess(e, contact)}
                      />
                      <span className="mt-n1">{`${contact.name} ${contact.surname}`}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
          {!!filterNoAccess?.length && (
            <>
              <span className="contacts-access-status">{t('contact_with_no_access')}</span>
              <div className="switch-section">
                <span className="switch-label">{t('documents_select_all_clients')}</span>
                <Switch
                  className="ml-2"
                  classNameMain=""
                  id="switchSelectAllNoAccess"
                  color={
                    selectedNoAccess.length === filterNoAccess.length
                      ? colors.pastelGreen
                      : colors.venetianRed
                  }
                  textColor={colors.black}
                  value={selectedNoAccess.length !== filterNoAccess.length}
                  onChange={handleSelectAllContactNoAccess}
                />
              </div>
              <div className="contacts-box mt-2">
                <ul className="contacts-container my-3">
                  {filterNoAccess?.map((contact) => (
                    <li className="mb-3 list-contact" key={contact.mail}>
                      <Checkbox
                        type="checkbox"
                        id="filterCheckAll"
                        label=""
                        checked={selectedNoAccess.some((file) => file.mail === contact.mail)}
                        onClick={(e) => handleSelectContactNoAccess(e, contact)}
                      />
                      <span className="mt-n1">{`${contact.name} ${contact.surname}`}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
          {isCustomer && <SwitchEmailToContacts selectedContacts={selectedAccess.length} />}
          {!filterAccess?.length && !filterNoAccess?.length && (
            <div className="empty-data-container">
              <UserIcon />
              <span>{t('client_contact_not_found')}</span>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="select-contacts">
            {!!contactsData?.length && (
              <div className="switch-section">
                <span className="switch-label">{t('documents_select_all_clients')}</span>
                <Switch
                  className="ml-2"
                  classNameMain=""
                  id="switchSelectAll"
                  color={
                    selectedContacts.length === contactsData.length
                      ? colors.pastelGreen
                      : colors.venetianRed
                  }
                  textColor={colors.black}
                  value={selectedContacts.length !== contactsData.length}
                  onChange={handleSelectAllContact}
                />
              </div>
            )}
          </div>
          <div className="contacts-box mt-2">
            <ul className="contacts-container my-3">
              {contactsData?.map((contact) => (
                <li className="mb-3 list-contact" key={contact.mail}>
                  <Checkbox
                    type="checkbox"
                    id="filterCheckAll"
                    label=""
                    checked={selectedContacts.some((file) => file.mail === contact.mail)}
                    onClick={(e) => handleSelectContact(e, contact)}
                  />
                  <span className="mt-n1">{`${contact.name} ${contact.surname}`}</span>
                </li>
              ))}
            </ul>
            {!contactsData?.length && (
              <div className="empty-data-container">
                <UserIcon />
                <span>{t('client_contact_not_found')}</span>
              </div>
            )}
          </div>
        </>
      )}
      {isCustomer && <SwitchEmailToContacts selectedContacts={selectedContacts.length} />}
      {isCustomer && selectedNoAccess.length > 0 && (
        <div className="switch-section">
          <span className="switch-label">{t('client_management_give_access')} </span>
          <Switch
            className="ml-2"
            id="switchGiveAccess"
            color={giveAccess ? colors.pastelGreen : colors.venetianRed}
            textColor={colors.black}
            value={!giveAccess}
            onChange={handleChange}
          />
        </div>
      )}
      {(selectedContacts.length > 0 ||
        selectedAccess.length > 0 ||
        selectedNoAccess.length > 0) && (
        <Container className="p-0">
          <Row>
            <Col xs={12} className="flex flex-row justify-content-end">
              <Button
                className="home__btn text-uppercase"
                type="button"
                label={t('documents_share_permission')}
                color="primary"
                onClick={doPublish}
              />
            </Col>
          </Row>
        </Container>
      )}
      {(loading || loadingUpdate) && <MainSpinner />}
    </>
  );
};

Contacts.propTypes = {
  onCancel: PropTypes.func.isRequired,
};

export default Contacts;
